import { stores } from '@kurtosys/app-start';
import { common } from '@kurtosys/ksys-app-template';
import { IManifest } from '@kurtosys/types/appsManager/index.js';
import { IArticle } from '@kurtosys/types/learning/IArticle';
import { ICompleteArticleRequest } from '@kurtosys/types/learning/ICompleteArticleRequest';
import { ICompleteArticleResponse } from '@kurtosys/types/learning/ICompleteArticleResponse';
import { TQuestions } from '@kurtosys/types/learning/TQuestions';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';

import { IComponentStyles } from '../../../models/app/IComponentStyles';
import { IConfiguration } from '../../../models/app/IConfiguration';
import { TStoreContext } from '../../../models/app/TStoreContext';
import { ResultsStore } from '../../Results/stores/ResultsStore';

import { AppStore } from './AppStore';
import { KurtosysApiStore } from './KurtosysApiStore';

const StoreBase = stores.base.StoreBase<IConfiguration, IComponentStyles>;

export type TUserAnswers = { [questionId: number]: number }; // Map QueryId to answer index
export enum QuizNavigation {
	backward = 'backward',
	forward = 'forward',
}

export class QuizStore extends StoreBase {
	@observable article: IArticle | undefined;
	@observable articleResults: ICompleteArticleResponse | undefined;
	@observable currentQuestion = 0;
	@observable userAnswers: TUserAnswers = {};
	@observable hasSubmitted = false;

	constructor(storeContext: TStoreContext, manifest: IManifest) {
		super(storeContext, manifest);
		makeObservable(this);
	}

	@action
	async initialize(): Promise<void> {
		this.fetchArticle();
	}

	@computed
	get appStore() {
		return this.storeContext.get<AppStore>('appStore');
	}

	@computed
	get kurtosysApiStore() {
		return this.storeContext.get<KurtosysApiStore>('kurtosysApiStore');
	}

	@computed
	get resultsStore() {
		return this.storeContext.get<ResultsStore>('resultsStore');
	}

	@computed
	get questions(): TQuestions {
		return (this.article && this.article.questions) || [];
	}

	@computed
	get totalQuestions(): number {
		return this.questions.length;
	}

	@computed
	get score(): number {
		const incorrectAnswers =
			(this.articleResults &&
				this.articleResults.incorrectAnswers &&
				this.articleResults.incorrectAnswers.length) ||
			0;
		return this.totalQuestions - incorrectAnswers;
	}

	@computed
	get percentage(): number {
		if (this.totalQuestions === 0) {
			return 0;
		}
		return this.score / this.totalQuestions;
	}

	@computed
	get currentQuestionId(): number {
		if (this.questions) {
			return this.questions[this.currentQuestion] && this.questions[this.currentQuestion].id;
		}
		return -1;
	}

	@computed
	get isComplete(): boolean {
		return !!(this.article && this.article.complete);
	}

	fetchArticle = async () => {
		const requestBody = { id: this.appStore.articleId };
		const results = await this.kurtosysApiStore.getArticle.execute({
			body: requestBody,
		});
		runInAction(() => {
			this.article = results;
		});

		if (this.article.complete && this.appStore.completedCardProps) {
			this.appStore.appCardProps = common.commonUtils.deepMergeObjectsWithOptions(
				{ arrayMergeStrategy: 'DeepMerge' },
				this.appStore.appCardProps,
				this.appStore.completedCardProps,
			);
			if (this.resultsStore.hideCommentaryOnComplete) {
				runInAction(() => {
					this.appStore.hideCommentary = true;
				});
			}
		}
		await this.checkUnstructuredQuiz();
	};

	@action
	restartQuiz = () => {
		if (this.article) {
			this.article = {
				...this.article,
				complete: false,
			};
			this.currentQuestion = 0;
			this.userAnswers = {};
		}
	};

	checkUnstructuredQuiz = async () => {
		// If there are no questions in the article, assume it is an unstructured quiz and complete the article
		if (this.article?.questions && this.article.questions.length === 0) {
			if (this.appStore.isRendered && !this.hasSubmitted) {
				this.hasSubmitted = true;
				const requestBody: ICompleteArticleRequest = {
					id: this.appStore.articleId,
					questions: [],
				};
				const results = await this.kurtosysApiStore.completeArticle.execute({
					body: requestBody,
				});

				runInAction(async () => {
					this.articleResults = results;
				});
			}
		}
	};

	completeQuiz = async () => {
		this.resultsStore.startCalculating();
		const requestBody: ICompleteArticleRequest = {
			id: this.appStore.articleId,
			questions: this.questions.map((question) => {
				return {
					id: question.id,
					answerIndex: this.userAnswers[question.id],
				};
			}),
		};

		const results = await this.kurtosysApiStore.completeArticle.execute({
			body: requestBody,
		});

		runInAction(async () => {
			this.articleResults = results;
			if (this.article) {
				this.article = {
					...this.article,
					complete: true,
				};
			}
			if (!this.articleResults.incorrectAnswers && this.resultsStore.hideCommentaryOnComplete) {
				this.appStore.hideCommentary = true;
			}
		});

		this.resultsStore.finishCalculating();
	};

	@action navigate(direction: QuizNavigation) {
		// Advance backward if not already on the first page
		if (direction === QuizNavigation.backward && this.currentQuestion > 0) {
			this.currentQuestion--;
		}
		// Advance forward if not already on the last page
		if (direction === QuizNavigation.forward && this.currentQuestion < this.totalQuestions - 1) {
			this.currentQuestion++;
		}
	}

	@action
	answerQuestion(answerIndex: number) {
		this.userAnswers = {
			...this.userAnswers,
			[this.currentQuestionId]: answerIndex,
		};
	}
}
